/**
 * Login Verify Page
 * 
 * OTP verification for passwordless authentication.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';

export default function LoginVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, isAuthenticated, error: authError } = useAuth();

  const { email, challengeId, returnTo } = (location.state as { email?: string; challengeId?: string; returnTo?: string }) || {};

  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!email || !challengeId) {
      navigate('/login', { replace: true });
    }
  }, [email, challengeId, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo || '/account', { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (error) trackEvent({ name: 'login_failed', properties: { reason: error } });
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !challengeId) return;

    setError(null);
    setIsLoading(true);

    try {
      await verifyOtp(email, otp, challengeId);
      trackEvent({ name: 'login_completed' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
      setOtp('');
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const displayError = error || authError;

  if (!email || !challengeId) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Verify Code | UKPropertyChecks</title>
        <meta name="description" content="Enter your verification code to sign in to UKPropertyChecks." />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/login/verify" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        <div className="container-main section-spacing">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-slate-950 mb-2">
                Enter verification code
              </h1>
              <p className="text-slate-600">
                We sent a 6-digit code to <strong>{email}</strong>
              </p>
            </div>

            <div className="card p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="otp" className="block text-sm font-semibold text-slate-950 mb-1">
                    Verification code
                  </label>
                  <input
                    ref={inputRef}
                    type="text"
                    id="otp"
                    name="otp"
                    value={otp}
                    onChange={handleOtpChange}
                    required
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    className={`input text-2xl text-center font-mono tracking-widest ${displayError ? 'border-red-500 border-l-4 border-l-[#dc2626]' : ''}`}
                    style={{ minHeight: '56px', letterSpacing: '0.5em' }}
                    placeholder="000000"
                    disabled={isLoading}
                    aria-invalid={displayError ? "true" : "false"}
                    aria-describedby={displayError ? "otp-error" : undefined}
                  />
                  <div className="h-6 mt-1">
                    {displayError && (
                      <p id="otp-error" className="text-sm font-medium text-red-700" role="alert">{displayError}</p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="btn-primary w-full"
                >
                  {isLoading ? 'Verifying...' : 'Verify code'}
                </button>
              </form>
            </div>

            <div className="mt-6 card-subtle p-4">
              <p className="text-sm text-slate-600">
                Didn't receive the code? Check your spam folder. The code expires in 10 minutes.
              </p>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-emerald-700 hover:text-emerald-800 underline text-sm"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
